import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import './CalendarView.css';
import CustomDropdown from '../../components/CustomDropdown';
import { usersAPI } from '../../api/api';

// Вспомогательные функции ДО компонента
const toMoscowDate = (dateString) => {
  if (!dateString) return new Date();
  const date = new Date(dateString);
  return new Date(date.getTime() + (3 * 60 * 60 * 1000)); // UTC+3
};

const getMoscowDateOnly = (dateString) => {
  const moscowDate = toMoscowDate(dateString);
  // Возвращаем только дату (без времени)
  return new Date(
    moscowDate.getFullYear(),
    moscowDate.getMonth(),
    moscowDate.getDate()
  );
};

const datesAreSameDay = (date1, date2) => {
  if (!date1 || !date2) return false;
  
  const d1 = getMoscowDateOnly(date1);
  const d2 = getMoscowDateOnly(date2);
  
  return d1.getTime() === d2.getTime();
};

const formatEmployeeDisplayName = (fullName = '') => {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return fullName;

  const [lastName, ...otherNames] = parts;
  const initials = otherNames
    .slice(0, 2)
    .map(name => `${name.charAt(0).toUpperCase()}.`)
    .join(' ');

  return `${lastName} ${initials}`.trim();
};

const EMPLOYEE_ORDER_CACHE_PREFIX = 'shifts-employee-order';

const getEmployeeOrderCacheKey = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    const userId = user?.id;
    const spaceId = user?.space_id;

    if (userId && spaceId) return `${EMPLOYEE_ORDER_CACHE_PREFIX}:${spaceId}:${userId}`;
    if (userId) return `${EMPLOYEE_ORDER_CACHE_PREFIX}:${userId}`;
  } catch (error) {
    // ignore local cache read errors
  }

  return `${EMPLOYEE_ORDER_CACHE_PREFIX}:anonymous`;
};

const readCachedEmployeeOrder = () => {
  try {
    const raw = localStorage.getItem(getEmployeeOrderCacheKey());
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

const writeCachedEmployeeOrder = (order) => {
  try {
    localStorage.setItem(getEmployeeOrderCacheKey(), JSON.stringify(order));
  } catch (error) {
    // ignore local cache write errors
  }
};

const CalendarView = ({ 
  shifts, 
  employees, 
  currentDate, 
  searchTerm, 
  onDateChange, 
  onDayClick, 
  onAddShift, 
  isAdmin, 
  onUpdateShiftComment 
}) => {
  const [viewMode, setViewMode] = useState('people');
  const [massEditMode, setMassEditMode] = useState(false);
  const [selectedShiftType, setSelectedShiftType] = useState('day');
  const [selectedShiftInfo, setSelectedShiftInfo] = useState(null);
  const [showAddEmployeeModal, setShowAddEmployeeModal] = useState(false);
  const [selectedEmployeeToAdd, setSelectedEmployeeToAdd] = useState('');
  
  // Состояния для временных данных
  const [temporaryEmployees, setTemporaryEmployees] = useState([]);
  const [pendingShifts, setPendingShifts] = useState([]);
  const [employeeOrder, setEmployeeOrder] = useState(() => readCachedEmployeeOrder());
  const [draggedEmployeeId, setDraggedEmployeeId] = useState(null);
  const [dragOverEmployeeId, setDragOverEmployeeId] = useState(null);
  const [isDraggingEmployee, setIsDraggingEmployee] = useState(false);
  const orderLoadedRef = useRef(false);
  const saveTimerRef = useRef(null);

  // Опции для кастомного пикера типов смен
  const shiftTypeOptions = [
    { value: 'day', label: 'Добавить Д (08:00-20:00)' },
    { value: 'day_9_18', label: 'Добавить Д (09:00-18:00)' },
    { value: 'day_9_21', label: 'Добавить Д (09:00-21:00)' },
    { value: 'day_11_20', label: 'Добавить Д (11:00-20:00)' },
    { value: 'day_11_23', label: 'Добавить Д (11:00-23:00)' },
    { value: 'night', label: 'Добавить Н' },
    { value: 'vacation', label: 'Добавить ОТ' },
    { value: 'unpaid', label: 'Добавить ДО' },
    { value: 'dayoff', label: 'Добавить В' },
    { value: 'sick', label: 'Добавить Б' },
    { value: 'holiday', label: 'Добавить НВ' },
    { value: 'delete', label: 'Удалить смену' }
  ];

  const productionCalendar = {
    2025: { 0: 136, 1: 160, 2: 167, 3: 175, 4: 144, 5: 151, 6: 184, 7: 168, 8: 176, 9: 184, 10: 151, 11: 176 },
    2026: { 0: 120, 1: 152, 2: 168, 3: 175, 4: 151, 5: 167, 6: 184, 7: 168, 8: 176, 9: 176, 10: 159, 11: 176 }
  };

  const getCurrentMonthNorm = useCallback(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    return productionCalendar[year]?.[month] ?? 151;
  }, [currentDate]);

  const dates = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    const dates = [];
    for (let day = 1; day <= lastDay.getDate(); day++) {
      dates.push(new Date(year, month, day));
    }
    
    return dates;
  }, [currentDate]);

  // ФИЛЬТРАЦИЯ СОТРУДНИКОВ ПО СТАТУСУ
  // Отображаем только активных сотрудников (status = 'active')
  const activeEmployees = useMemo(() => {
    
    
    // Фильтруем только активных сотрудников
    const activeEmployees = employees.filter(employee => 
      employee.status === 'active' || employee.status === undefined // на случай, если статус не установлен
    );
    
  
    
    return activeEmployees;
  }, [employees, shifts]);

  useEffect(() => {
    let mounted = true;
    const loadOrder = async () => {
      try {
        const response = await usersAPI.getEmployeeOrder();
        const order = Array.isArray(response?.order) ? response.order : [];
        if (mounted) {
          setEmployeeOrder(order);
          writeCachedEmployeeOrder(order);
        }
      } catch (error) {
        // ignore load errors, allow drag to re-save later
      } finally {
        orderLoadedRef.current = true;
      }
    };
    loadOrder();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const activeIds = activeEmployees.map(employee => employee.id);
    setEmployeeOrder(prevOrder => {
      const filtered = prevOrder.filter(id => activeIds.includes(id));
      const missing = activeIds.filter(id => !filtered.includes(id));
      return [...filtered, ...missing];
    });
  }, [activeEmployees]);

  useEffect(() => {
    writeCachedEmployeeOrder(employeeOrder);
  }, [employeeOrder]);

  useEffect(() => {
    if (employeeOrder.length === 0) return;
    if (!orderLoadedRef.current) return;
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }
    saveTimerRef.current = setTimeout(async () => {
      try {
        await usersAPI.setEmployeeOrder(employeeOrder);
      } catch (error) {
        // ignore save errors
      }
    }, 400);
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
    };
  }, [employeeOrder]);

  const employeesToDisplay = useMemo(() => {
    if (employeeOrder.length === 0) return activeEmployees;
    const orderIndex = new Map(employeeOrder.map((id, index) => [id, index]));
    return [...activeEmployees].sort((a, b) => {
      const indexA = orderIndex.get(a.id);
      const indexB = orderIndex.get(b.id);
      return (indexA ?? Number.MAX_SAFE_INTEGER) - (indexB ?? Number.MAX_SAFE_INTEGER);
    });
  }, [activeEmployees, employeeOrder]);

  const handleDragStart = (employeeId) => {
    if (!massEditMode) return;
    setDraggedEmployeeId(employeeId);
    setIsDraggingEmployee(true);
  };

  const handleDragOver = (event, employeeId) => {
    if (!massEditMode) return;
    event.preventDefault();
    if (employeeId === draggedEmployeeId) {
      setDragOverEmployeeId(null);
      return;
    }
    setDragOverEmployeeId(employeeId);

    if (!draggedEmployeeId) return;
    setEmployeeOrder(prevOrder => {
      const baseOrder = prevOrder.length > 0 ? prevOrder : activeEmployees.map(employee => employee.id);
      const fromIndex = baseOrder.indexOf(draggedEmployeeId);
      const toIndex = baseOrder.indexOf(employeeId);
      if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return prevOrder;

      const nextOrder = [...baseOrder];
      nextOrder.splice(fromIndex, 1);
      nextOrder.splice(toIndex, 0, draggedEmployeeId);
      return nextOrder;
    });
  };

  const handleDragLeave = () => {
    if (!massEditMode) return;
    setDragOverEmployeeId(null);
  };

  const handleDrop = (event, targetEmployeeId) => {
    if (!massEditMode) return;
    event.preventDefault();
    setDraggedEmployeeId(null);
    setDragOverEmployeeId(null);
    setIsDraggingEmployee(false);
  };

  const handleDragEnd = () => {
    setDraggedEmployeeId(null);
    setDragOverEmployeeId(null);
    setIsDraggingEmployee(false);
  };

  // УПРОЩЕННАЯ версия getEmployeeShift
  const getEmployeeShift = useCallback((employeeId, date) => {
    // Сначала проверяем ожидающие смены
    const pendingShift = pendingShifts.find(pending => {
      const pendingDate = new Date(pending.date);
      const targetDate = new Date(date);

      // Приводим к началу дня
      pendingDate.setHours(0, 0, 0, 0);
      targetDate.setHours(0, 0, 0, 0);

      return pending.employeeId === employeeId &&
             pendingDate.getTime() === targetDate.getTime();
    });

    if (pendingShift) {
      return pendingShift.shiftData;
    }

    // Ищем существующую смену
    return shifts.find(shift => {
      if (shift.user_id !== employeeId) return false;

      const shiftDate = new Date(shift.start_time);
      const targetDate = new Date(date);

      // Приводим к началу дня
      shiftDate.setHours(0, 0, 0, 0);
      targetDate.setHours(0, 0, 0, 0);

      return shiftDate.getTime() === targetDate.getTime();
    });
  }, [shifts, pendingShifts]);

  // Поиск смены ТОЛЬКО в БД (без pendingShifts) — для определения shiftId
  const getEmployeeShiftFromDB = useCallback((employeeId, date) => {
    return shifts.find(shift => {
      if (shift.user_id !== employeeId) return false;

      const shiftDate = new Date(shift.start_time);
      const targetDate = new Date(date);

      shiftDate.setHours(0, 0, 0, 0);
      targetDate.setHours(0, 0, 0, 0);

      return shiftDate.getTime() === targetDate.getTime();
    });
  }, [shifts]);

  // Создание данных смены
  const createShiftData = useCallback((shiftData) => {
    const { date, shiftType } = shiftData;
    const startTime = new Date(date);
    let endTime = new Date(date);

    let description = '';

    if (shiftType === 'day') {
      startTime.setHours(8, 0, 0, 0);
      endTime.setHours(20, 0, 0, 0);
      description = 'Дневная смена (08:00 - 20:00)';
    } else if (shiftType === 'day_9_18') {
      startTime.setHours(9, 0, 0, 0);
      endTime.setHours(18, 0, 0, 0);
      description = 'Дневная смена (09:00 - 18:00)';
    } else if (shiftType === 'day_9_21') {
      startTime.setHours(9, 0, 0, 0);
      endTime.setHours(21, 0, 0, 0);
      description = 'Дневная смена (09:00 - 21:00)';
    } else if (shiftType === 'day_11_20') {
      startTime.setHours(11, 0, 0, 0);
      endTime.setHours(20, 0, 0, 0);
      description = 'Дневная смена (11:00 - 20:00)';
    } else if (shiftType === 'day_11_23') {
      startTime.setHours(11, 0, 0, 0);
      endTime.setHours(23, 0, 0, 0);
      description = 'Дневная смена (11:00 - 23:00)';
    } else if (shiftType === 'night') {
      startTime.setHours(20, 0, 0, 0);
      endTime = new Date(startTime);
      endTime.setDate(endTime.getDate() + 1);
      endTime.setHours(8, 0, 0, 0);
      description = 'Ночная смена (20:00 - 08:00)';
    } else if (shiftType === 'delete') {
      return null;
    } else {
      startTime.setHours(0, 0, 0, 0);
      endTime.setHours(23, 59, 59, 999);

      const descriptions = {
        'vacation': 'Отпуск',
        'unpaid': 'Отпуск без сохранения заработной платы',
        'dayoff': 'Выходной день',
        'sick': 'Больничный лист',
        'holiday': 'День отдыха в счет дня работы в праздничный день'
      };
      description = descriptions[shiftType] || '';
    }

    return {
      start_time: startTime.toISOString(),
      end_time: endTime.toISOString(),
      description: description
    };
  }, []);

  // Обработчик клика по ячейке смены
  const handleShiftCellClick = useCallback((employee, date) => {
    if (massEditMode) {
      // Для shiftId ищем ТОЛЬКО в БД, игнорируя pendingShifts
      // иначе при повторном клике shiftData без id теряет оригинальный shiftId
      const existingShift = getEmployeeShiftFromDB(employee.id, date);

      if (selectedShiftType === 'delete') {
        if (existingShift) {
          setPendingShifts(prev => {
            const filtered = prev.filter(pending => 
              !(pending.employeeId === employee.id && 
                new Date(pending.date).toDateString() === date.toDateString())
            );
            
            return [...filtered, {
              employeeId: employee.id,
              date: date,
              shiftData: null,
              shiftId: existingShift.id
            }];
          });
        }
      } else {
        const shiftData = {
          employeeId: employee.id,
          date: date,
          shiftType: selectedShiftType
        };
        
        const newShiftData = createShiftData(shiftData);
        
        setPendingShifts(prev => {
          const filtered = prev.filter(pending => 
            !(pending.employeeId === employee.id && 
              new Date(pending.date).toDateString() === date.toDateString())
          );
          
          return [...filtered, {
            employeeId: employee.id,
            date: date,
            shiftData: newShiftData,
            shiftId: existingShift?.id
          }];
        });
      }
    } else {
      const shift = getEmployeeShift(employee.id, date);
      setSelectedShiftInfo({
        employee,
        shift,
        date
      });
    }
  }, [massEditMode, selectedShiftType, getEmployeeShiftFromDB, createShiftData]);

  const handleFinishMassEdit = useCallback(async () => {
    try {
      const operations = pendingShifts.map(pendingShift => {
        if (pendingShift.shiftData === null) {
          return { action: 'delete', id: pendingShift.shiftId };
        }
        const { start_time, end_time, description } = pendingShift.shiftData;
        return {
          action: pendingShift.shiftId ? 'update' : 'create',
          id: pendingShift.shiftId,
          data: {
            startTime: start_time,
            endTime: end_time,
            description: description || '',
            userId: pendingShift.employeeId
          }
        };
      });

      await onAddShift({ batch: operations });

      setPendingShifts([]);
      setMassEditMode(false);
      setSelectedShiftType('day');
    } catch (error) {
      alert('Ошибка при сохранении изменений');
    }
  }, [pendingShifts, onAddShift]);

  // Получение типа смены из описания
    const getShiftTypeFromDescription = useCallback((description) => {
      if (!description) return 'day';

      if (description.includes('Дневная смена (09:00 - 21:00)')) return 'day_9_21';
      if (description.includes('Дневная смена (11:00 - 20:00)')) return 'day_11_20';
      if (description.includes('Дневная смена (11:00 - 23:00)')) return 'day_11_23';
      if (description.includes('Дневная смена (09:00 - 18:00)')) return 'day_9_18';
      if (description.includes('Дневная смена (08:00 - 20:00)')) return 'day';
      if (description.includes('Ночная смена')) return 'night';
      if (description.includes('Отпуск') && !description.includes('без сохранения')) return 'vacation';
      if (description.includes('без сохранения')) return 'unpaid';
      if (description.includes('Выходной')) return 'dayoff';
      if (description.includes('Больничный')) return 'sick';
      if (description.includes('праздничный')) return 'holiday';
      return 'day';
  }, []);

  // Форматирование типа смены для отображения
  const formatShiftType = useCallback((shift) => {
    if (!shift) return '';
    
    if (shift.description) {
      if (shift.description.includes('Дневная смена (09:00 - 21:00)')) return 'Д9-21';
      if (shift.description.includes('Дневная смена (11:00 - 20:00)')) return 'Д11-20';
      if (shift.description.includes('Дневная смена (11:00 - 23:00)')) return 'Д11-23';
      if (shift.description.includes('Отпуск') && !shift.description.includes('без сохранения')) return 'ОТ';
      if (shift.description.includes('без сохранения')) return 'ДО';
      if (shift.description.includes('Выходной')) return 'В';
      if (shift.description.includes('Больничный')) return 'Б';
      if (shift.description.includes('праздничный')) return 'НВ';
      if (shift.description.includes('Ночная смена (20:00 - 08:00)')) return 'Н';
    }
    
    const startTime = new Date(shift.start_time);
    const endTime = new Date(shift.end_time);
    const startHours = startTime.getHours();
    const endHours = endTime.getHours();
    if (startHours === 8 && endHours === 20) return 'Д';
    if (startHours === 9 && endHours === 18) return 'Д8';
    if (startHours === 9 && endHours === 21) return 'Д9-21';
    if (startHours === 11 && endHours === 20) return 'Д11-20';
    if (startHours === 11 && endHours === 23) return 'Д11-23';
    if (startHours === 20) return 'Н';
    
    return 'Д';
  }, []);

  const calculateShiftHours = useCallback((shift) => {
    if (!shift) return 0;
    const shiftType = formatShiftType(shift);
    if (shiftType === 'Д') return 11;
    if (shiftType === 'Д8') return 8;
    if (shiftType === 'Д9-21') return 11;
    if (shiftType === 'Д11-20') return 8;
    if (shiftType === 'Д11-23') return 11;
    if (shiftType === 'Н') return 11;
    return 0;
  }, [formatShiftType]);

  const calculateEmployeeStats = useCallback((employeeId) => {
    const realShifts = shifts.filter(shift => shift.user_id === employeeId);
    const pendingForEmployee = pendingShifts.filter(pending => 
      pending.employeeId === employeeId && pending.shiftData !== null
    );
    
    const allShifts = [...realShifts, ...pendingForEmployee.map(p => p.shiftData)];
    
    const workingShifts = allShifts.filter(shift => {
      const shiftType = formatShiftType(shift);
      return (
        shiftType === 'Д' ||
        shiftType === 'Д8' ||
        shiftType === 'Д9-21' ||
        shiftType === 'Д11-20' ||
        shiftType === 'Д11-23' ||
        shiftType === 'Н'
      );
    });
    
    const dayShifts = workingShifts.filter(shift => formatShiftType(shift) === 'Д');
    const day8Shifts = workingShifts.filter(shift => formatShiftType(shift) === 'Д8');
    const day9_21Shifts = workingShifts.filter(shift => formatShiftType(shift) === 'Д9-21');
    const day11_20Shifts = workingShifts.filter(shift => formatShiftType(shift) === 'Д11-20');
    const day11_23Shifts = workingShifts.filter(shift => formatShiftType(shift) === 'Д11-23');
    const nightShifts = workingShifts.filter(shift => formatShiftType(shift) === 'Н');
    
    const totalHours = workingShifts.reduce((total, shift) => total + calculateShiftHours(shift), 0);
    const norm = getCurrentMonthNorm();
    const deviation = totalHours - norm;

      return {
        totalShifts: workingShifts.length,
        dayShifts: dayShifts.length,
        day8Shifts: day8Shifts.length,
        day9_21Shifts: day9_21Shifts.length,
        day11_20Shifts: day11_20Shifts.length,
        day11_23Shifts: day11_23Shifts.length,
        nightShifts: nightShifts.length,
        totalHours: Math.round(totalHours),
        norm: norm,
        deviation: deviation
    };
  }, [shifts, pendingShifts, formatShiftType, calculateShiftHours, getCurrentMonthNorm]);

  // Модальное окно информации о смене
  const ShiftInfoModal = () => {
    if (!selectedShiftInfo) return null;

    const { employee, shift, date } = selectedShiftInfo;
    const [localComment, setLocalComment] = useState(shift?.comment || '');

    const handleSaveComment = () => {
      if (shift && isAdmin && onUpdateShiftComment) {
        onUpdateShiftComment(shift.id, localComment);
        setSelectedShiftInfo(null);
      }
    };

    const handleClose = () => {
      setSelectedShiftInfo(null);
    };

    return (
      <div className="modal-overlay" onClick={handleClose}>
        <div className="modal shift-info-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>Информация о смене</h3>
            <button className="close-btn" onClick={handleClose}>
              <i className="fas fa-times"></i>
            </button>
          </div>
          
          <div className="modal-content">
            <div className="shift-details">
              <div className="detail-section">
                <h4>Информация о сотруднике:</h4>
                <div className="detail-row">
                  <strong>ФИО:</strong>
                  <span>{employee.username}</span>
                </div>
                <div className="detail-row">
                  <strong>Должность:</strong>
                  <span>{employee.position}</span>
                </div>
                <div className="detail-row">
                  <strong>Отдел:</strong>
                  <span>{employee.department || 'Не указан'}</span>
                </div>
                <div className="detail-row">
                  <strong>Статус:</strong>
                  <span className={`status-badge ${employee.status === 'active' ? 'active' : 'inactive'}`}>
                    {employee.status === 'active' ? 'Активный' : 'Неактивный'}
                  </span>
                </div>
              </div>

              <div className="detail-section">
                <h4>Информация о смене:</h4>
                <div className="detail-row">
                  <strong>Дата:</strong>
                  <span>{date.toLocaleDateString('ru-RU')}</span>
                </div>
                <div className="detail-row">
                  <strong>Тип смены:</strong>
                  <span>{formatShiftType(shift)}</span>
                </div>
                {shift && (
                  <>
                    <div className="detail-row">
                      <strong>Описание:</strong>
                      <span>{shift.description}</span>
                    </div>
                    <div className="detail-row">
                      <strong>Время:</strong>
                      <span>
                        {new Date(shift.start_time).toLocaleTimeString('ru-RU', { 
                          hour: '2-digit', minute: '2-digit' 
                        })} - {new Date(shift.end_time).toLocaleTimeString('ru-RU', { 
                          hour: '2-digit', minute: '2-digit' 
                        })}
                      </span>
                    </div>
                  </>
                )}
              </div>

              {shift?.comment && (
                <div className="detail-section">
                  <h4>Комментарий к смене:</h4>
                  <div className="shift-comment">
                    <div className="shift-comment-content">
                      {(shift.comment)}
                    </div>
                  </div>
                </div>
              )}

              {isAdmin && (
                <div className="detail-section">
                  <h4>Редактировать комментарий:</h4>
                  <textarea
                    className="comment-textarea"
                    value={localComment}
                    onChange={(e) => setLocalComment(e.target.value)}
                    placeholder="Введите комментарий к смене..."
                    rows="4"
                  />
                </div>
              )}
            </div>
          </div>
          
          <div className="modal-footer">
            {isAdmin && (
              <button className="add-button" onClick={handleSaveComment}>
                <i className="fas fa-save"></i>
                Сохранить комментарий
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const getShiftCellClass = (shiftType) => {
    switch(shiftType) {
      case 'Д': return 'day-shift';
      case 'Д8': return 'day8-shift';
      case 'Д9-21': return 'day9-21-shift';
      case 'Д11-20': return 'day11-20-shift';
      case 'Д11-23': return 'day11-23-shift';
      case 'Н': return 'night-shift';
      case 'ОТ': return 'vacation-shift';
      case 'ДО': return 'unpaid-shift';
      case 'В': return 'dayoff-shift';
      case 'Б': return 'sick-shift';
      case 'НВ': return 'holiday-shift';
      default: return 'other-shift';
    }
  };
  
  const shiftLegend = [
    { code: 'Д', description: 'Дневная смена (с 08:00 до 20:00)' },
    { code: 'Д8', description: 'Дневная смена (с 09:00 до 18:00)' },
    { code: 'Д9-21', description: 'Дневная смена (с 09:00 до 21:00)' },
    { code: 'Д11-20', description: 'Дневная смена (с 11:00 до 20:00)' },
    { code: 'Д11-23', description: 'Дневная смена (с 11:00 до 23:00)' },
    { code: 'Н', description: 'Ночная смена (с 20:00 до 08:00)' },
    { code: 'ОТ', description: 'Отпуск' },
    { code: 'ДО', description: 'Отпуск без сохранения заработной платы' },
    { code: 'В', description: 'Выходной день' },
    { code: 'Б', description: 'Больничный лист' },
    { code: 'НВ', description: 'День отдыха в счет дня работы в праздничный день' }
  ];

  const PeopleView = () => (
    <div className="people-view">
      {isAdmin && (
        <div className="calendar-controls">
          <div className="mass-edit-controls">
            <button 
              className={`btn-delete ${massEditMode ? 'active' : ''}`}
              onClick={() => massEditMode ? handleFinishMassEdit() : setMassEditMode(true)}
            >
              <i className="fas fa-edit"></i>
              {massEditMode ? 'Завершить редактирование' : 'Массовое редактирование!'}
            </button>

            {massEditMode && (
              <>
                <div className="shift-type-selector">
                  <label>Действие:</label>
                  <CustomDropdown
                    options={shiftTypeOptions}
                    value={selectedShiftType}
                    onChange={setSelectedShiftType}
                    placeholder="Выберите действие..."
                  />
                </div>
                <div className="mass-edit-info">
                  <span>Ожидающие сохранения: {pendingShifts.length}</span>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <div className="table-container">
        <table className="shifts-table">
          <thead>
            <tr>
              <th rowSpan="2" className="employee-header">
                <div className="employee-header-content">
                  <span>Должность / Сотрудник</span>
                </div>
              </th>
              <th colSpan={dates.length} className="dates-header">
                {/* Убрали название периода */}
              </th>
              <th colSpan="10" className="stats-header">Итого смен</th>
            </tr>
            <tr>
              {dates.map(date => (
                <th key={date.toISOString()} className="date-header">
                  <div className="date-number">{date.getDate()}</div>
                  <div className="day-of-week">
                    {['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'][date.getDay()]}
                  </div>
                </th>
              ))}
              <th className="stat-column" rowSpan="2">Д</th>
              <th className="stat-column" rowSpan="2">Д8</th>
              <th className="stat-column" rowSpan="2">Д9-21</th>
              <th className="stat-column" rowSpan="2">Д11-20</th>
              <th className="stat-column" rowSpan="2">Д11-23</th>
              <th className="stat-column" rowSpan="2">Н</th>
              <th className="stat-column" rowSpan="2">Смены</th>
              <th className="stat-column" rowSpan="2">Часы</th>
              <th className="stat-column" rowSpan="2">Норма</th>
              <th className="stat-column" rowSpan="2">Отклонение</th>
            </tr>
          </thead>
          <tbody>
            {employeesToDisplay.length === 0 ? (
              <tr>
                  <td colSpan={dates.length + 11} className="no-employees-message">
                  <div className="no-employees-content">
                    <i className="fas fa-users-slash"></i>
                    <span>Нет активных сотрудников для отображения</span>
                  </div>
                </td>
              </tr>
            ) : (
              employeesToDisplay.map(employee => {
                const stats = calculateEmployeeStats(employee.id);
                const isTemporary = temporaryEmployees.some(temp => temp.id === employee.id);
                
                return (
                  <tr
                    key={employee.id}
                    className={`employee-row ${massEditMode ? 'draggable' : ''} ${dragOverEmployeeId === employee.id ? 'drag-over' : ''} ${draggedEmployeeId === employee.id ? 'drag-selected' : ''}`}
                    onDragOver={(event) => handleDragOver(event, employee.id)}
                    onDragLeave={handleDragLeave}
                    onDrop={(event) => handleDrop(event, employee.id)}
                  >
                    <td className="employee-combined-cell">
                      <div
                        className="employee-combined-info"
                        draggable={massEditMode}
                        onDragStart={() => handleDragStart(employee.id)}
                        onDragEnd={handleDragEnd}
                      >
                        {massEditMode && (
                          <span
                            className={`drag-handle ${isDraggingEmployee ? 'dragging' : ''}`}
                            title="Перетащить"
                          >
                            <i className="fas fa-grip-lines"></i>
                          </span>
                        )}
                        <div className="employee-position">{employee.position}</div>
                        <div className="employee-name">{formatEmployeeDisplayName(employee.username)}</div>
                        {isTemporary && (
                          <div className="temporary-badge">новый</div>
                        )}
                      </div>
                    </td>
                    
                    {dates.map(date => {
                      const shift = getEmployeeShift(employee.id, date);
                      const shiftType = shift ? formatShiftType(shift) : '';
                      const isDayShift = shiftType === 'Д';
                      const isDay9Shift = shiftType === 'Д8';
                      const isDay9_21Shift = shiftType === 'Д9-21';
                      const isDay11_20Shift = shiftType === 'Д11-20';
                      const isDay11_23Shift = shiftType === 'Д11-23';
                      const isNightShift = shiftType === 'Н';
                      const isVacationShift = shiftType === 'ОТ';
                      const isUnpaidShift = shiftType === 'ДО';
                      const isDayoffShift = shiftType === 'В';
                      const isSickShift = shiftType === 'Б';
                      const isHolidaykShift = shiftType === 'НВ';
                      
                      const isPendingShift = pendingShifts.some(pending => {
                        const pendingDate = new Date(pending.date);
                        return pending.employeeId === employee.id && 
                              pendingDate.toDateString() === date.toDateString();
                      });
                      
                      return (
                        <td 
                          key={date.toISOString()} 
                          className={`shift-cell ${
                            shift ? (
                              isDayShift ? 'day-shift' : 
                              isDay9Shift ? 'day8-shift' :
                              isDay9_21Shift ? 'day9-21-shift' :
                              isDay11_20Shift ? 'day11-20-shift' :
                              isDay11_23Shift ? 'day11-23-shift' :
                              isNightShift ? 'night-shift' : 
                              isVacationShift ? 'vacation-shift' : 
                              isUnpaidShift ? 'unpaid-shift' :
                              isDayoffShift ? 'dayoff-shift' :
                              isSickShift ? 'sick-shift' :
                              isHolidaykShift ? 'holiday-shift' : 'other-shift'
                            ) : ''
                          } ${massEditMode ? 'editable' : ''} ${
                            isPendingShift ? 'pending-shift' : ''
                          }`}
                          title={shift ? 
                            `${shiftType} - ${shift.description || 'Смена'}` 
                            : `Пусто\n${date.toLocaleDateString('ru-RU')}`
                          }
                          onClick={() => handleShiftCellClick(employee, date)}
                        >
                          {shiftType}
                          {massEditMode && !shift && !isPendingShift && (
                            <div className="add-indicator">+</div>
                          )}
                        </td>
                      );
                    })}
                    
                    <td className="stat-cell days-shifts">{stats.dayShifts}</td>
                    <td className="stat-cell day8-shifts">{stats.day8Shifts}</td>
                    <td className="stat-cell day9-21-shifts">{stats.day9_21Shifts}</td>
                    <td className="stat-cell day11-20-shifts">{stats.day11_20Shifts}</td>
                    <td className="stat-cell day11-23-shifts">{stats.day11_23Shifts}</td>
                    <td className="stat-cell night-shifts">{stats.nightShifts}</td>
                    <td className="stat-cell total-shifts">{stats.totalShifts}</td>
                    <td className="stat-cell total-hours">{stats.totalHours}</td>
                    <td className="stat-cell norm">{stats.norm}</td>
                    <td className={`stat-cell deviation ${stats.deviation >= 0 ? 'positive' : 'negative'}`}>
                      {stats.deviation}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Легенда смен - всегда открыта */}
      <div className="shift-legend">
        <h4>Легенда смен:</h4>
        <div className="legend-grid">
          {shiftLegend.map((item, index) => {
            // Объект для маппинга кодов на классы
          const legendClassMap = {
            'Д': 'day-legend',
            'Д8': 'day8-legend',
            'Д9-21': 'day9-21-legend',
            'Д11-20': 'day11-20-legend',
            'Д11-23': 'day11-23-legend',
            'Н': 'night-legend',
            'ОТ': 'vacation-legend',
            'ДО': 'unpaid-legend',
              'В': 'dayoff-legend',
              'Б': 'sick-legend',
              'НВ': 'holiday-legend'
            };
            
            const code = item.code.trim();
            const legendClass = legendClassMap[code] || 'special-legend';
            
            return (
              <div key={index} className="legend-item">
                <span className={`legend-code ${legendClass}`}>
                  {item.code}
                </span>
                <span className="legend-description">{item.description}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Модальные окна */}
      <ShiftInfoModal />
    </div>
  );

  return (
    <div className="calendar-view">
      <PeopleView />
    </div>
  );
};

export default CalendarView;
